/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Pressable, Text, Icon, Radiobutton } from '@components/atoms'
import { IconPicker, Modal } from '@components/molecules'
import { DndTree } from '@components/systems'
import { mono, boFont } from '@components/systems/backoffice/backoffice.tokens'

import { Path } from '@utils'

import {
	useAppDispatch,
	useAppSelector,
	EditMenuType,
	adminActions,
	MenuTreeNodeType,
	CrudType,
} from '@redux/index'
import { produce } from 'immer'

/** 메뉴 편집 모달 7종(상위 선택 · 아이콘 · 중복 이름 · 저장 · 삭제 …). 문장은 그대로, 색만 걷어냈다 */
export const AdminEditMenuModal = () => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// redux state
	const crud: CrudType = useAppSelector(state => state.auth.currentAccess)
	const editTempMenu: EditMenuType = useAppSelector(state => state.admin.editTempMenu)
	const menuOpenModal: string = useAppSelector(state => state.admin.menuOpenModal)
	const unduplicatedName: string = useAppSelector(state => state.admin.unduplicatedName)
	const menuTreeList = useAppSelector(state => state.admin.menuTreeList)
	const selectedMenu: EditMenuType = useAppSelector(state => state.admin.editTempMenu)

	// state
	const [showModal, setShowModal] = React.useState<boolean>(false)
	const [modalInfo, setModalInfo] = React.useState({ target: '', title: '' })
	const [isCheckedRecommendedName, setIsCheckedRecommendedName] =
		React.useState<boolean>(false)
	const [tempSelectedMenuParent, setTempSelectedMenuParent] =
		React.useState<string>('root')

	// variables
	const isDir: boolean = editTempMenu.id.startsWith('MD-')
	const isAllowDeleteDir: boolean = crud.includes('D') !== null && isDir
	const isAllowDeleteObject: boolean = crud.includes('D') !== null && !isDir

	const showSubmit: boolean =
		(modalInfo.target === 'deletingDirSubmit' && isAllowDeleteDir) ||
		(modalInfo.target === 'deletingObjectSubmit' && isAllowDeleteObject) ||
		true
	const submitTitle: string = React.useMemo(
		() =>
			['menuTree', 'duplicatedNameAlert'].includes(modalInfo.target)
				? '선택'
				: ['deletingDirSubmit', 'deletingObjectSubmit'].includes(modalInfo.target)
				? '삭제'
				: '저장',
		[modalInfo.target],
	)
	const closeTitle: string = React.useMemo(
		() =>
			['duplicatedNameWithMovingAlert'].includes(modalInfo.target) ? '닫기' : '취소',
		[modalInfo.target],
	)

	React.useEffect(() => {
		if (menuOpenModal !== '') {
			onOpenModal(menuOpenModal)
		}
	}, [menuOpenModal])

	const onOpenModal = (target: string) => {
		const newModalInfo = produce(modalInfo, draft => {
			draft.target = target

			if (target === 'menuTree') {
				draft.title = '상위 메뉴 선택'
			} else if (target === 'iconPicker') {
				draft.title = '아이콘 선택'
			} else if (target === 'duplicatedNameAlert') {
				draft.title = '같은 이름의 폴더가 존재합니다.'
			} else if (target === 'updatingSubmit') {
				draft.title = '메뉴 변경 저장'
			} else if (target === 'duplicatedNameWithMovingAlert') {
				draft.title = '중복되는 이름 발견'
			} else if (target === 'deletingDirSubmit') {
				draft.title = '메뉴 삭제'
			} else if (target === 'deletingObjectSubmit') {
				draft.title = '메뉴 오브젝트 삭제'
			}
		})

		setModalInfo(newModalInfo)
		setShowModal(true)
	}

	const onCloseModal = () => {
		reduxDispatch(adminActions.MENU_OPEN_MODAL(''))
		setShowModal(false)

		if (modalInfo.target === 'duplicatedNameWithMovingAlert') {
			reduxDispatch(adminActions.TRIGGER_GET_MENU_LIST(true))
		}
	}

	const onMenuTreeRadioChange = (value: string | number) => {
		const tempParentId: string = value.toString() ?? value
		setTempSelectedMenuParent(tempParentId)
	}

	const onIconPickerSubmit = (iconName: string) => {
		onCloseModal()

		const newMenu: EditMenuType = {
			...selectedMenu,
			icon: iconName,
		}

		reduxDispatch(adminActions.EDIT_TEMP_MENU({ ...newMenu }))
	}

	const onSubmit = () => {
		if (modalInfo.target === 'menuTree') {
			onSelectMenuTreeClick()
		} else if (modalInfo.target === 'duplicatedNameAlert') {
			onChangeMenuName()
		} else if (modalInfo.target === 'updatingSubmit') {
			onUpdatingMenuSubmit()
		} else if (modalInfo.target === 'deletingDirSubmit') {
			onDeletingMenuDirSubmit()
		} else if (modalInfo.target === 'deletingObjectSubmit') {
			onDeletingMenuObjectSubmit()
		}
	}

	const onSelectMenuTreeClick = () => {
		onCloseModal()

		const newPath: string = Path.getPathByParentIdAndName({
			menuTreeList: menuTreeList,
			parentId: tempSelectedMenuParent,
			name: selectedMenu.name,
		})
		const newUri: string = Path.nextUri(selectedMenu.uri, selectedMenu.path, newPath)

		const newMenu: EditMenuType = {
			...selectedMenu,
			parent: tempSelectedMenuParent,
			path: newPath,
			uri: newUri,
		}

		reduxDispatch(adminActions.EDIT_TEMP_MENU({ ...newMenu }))
	}

	const onChangeMenuName = () => {
		const newPath: string = Path.getPathByName(selectedMenu.path, unduplicatedName)
		const newUri: string = Path.nextUri(selectedMenu.uri, selectedMenu.path, newPath)
		const newMenu: EditMenuType = produce(selectedMenu, draft => {
			draft.name = unduplicatedName
			draft.path = newPath
			draft.uri = newUri
		})

		reduxDispatch(adminActions.EDIT_TEMP_MENU({ ...newMenu }))
	}

	const onUpdatingMenuSubmit = () => {
		onCloseModal()
		reduxDispatch(adminActions.TRIGGER_MODIFY_MENU(true))
	}

	const onDeletingMenuDirSubmit = () => {
		onCloseModal()
		if (isAllowDeleteDir) {
			reduxDispatch(adminActions.TRIGGER_DELETE_MENU(true))
		}
	}

	const onDeletingMenuObjectSubmit = () => {
		onCloseModal()
		if (isAllowDeleteObject) {
			reduxDispatch(adminActions.TRIGGER_DELETE_MENU(true))
		}
	}

	const onNodeClick = (node: MenuTreeNodeType) => {
		onMenuTreeRadioChange(node.id)
	}

	return (
		<Modal
			show={showModal}
			target={modalInfo.target}
			title={modalInfo.title}
			onCloseModal={onCloseModal}
			closeTitle={closeTitle}
			submitTitle={submitTitle}
			isTitleDanger={['deletingDirSubmit', 'deletingObjectSubmit'].includes(
				modalInfo.target,
			)}
			closeStatus="inherit"
			submitStatus={
				['deletingDirSubmit', 'deletingObjectSubmit'].includes(modalInfo.target)
					? 'error'
					: 'primary'
			}
			showSubmit={showSubmit}
			onSubmit={onSubmit}
			style={[
				modalInfo.target === 'menuTree'
					? {
							width: '30rem',
							height: '30rem',
					  }
					: modalInfo.target === 'iconPicker'
					? {
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'flex-start',
							width: 450,
							maxHeight: 470,
					  }
					: {},
			]}
		>
			{modalInfo.target === 'menuTree' ? (
				<View css={style}>
					<View className="menuRootDirView">
						<Pressable
							className="menuRootDirButton"
							onClick={() => onMenuTreeRadioChange('root')}
						>
							<Icon name="folder-outline" className="menuRootDirIcon" />
							<span className="menuRootDirText">/ (최상위 폴더)</span>
						</Pressable>
						<View className="menuRootDirRadiobutton">
							<Radiobutton
								value={'root'}
								color={mono.ink}
								onRadioChange={onMenuTreeRadioChange}
								selectedValue={tempSelectedMenuParent}
							/>
						</View>
					</View>
					<DndTree
						data={[...menuTreeList]}
						rootId="root"
						showCheckBox
						hideObject
						disableDragging
						selectedRadio={tempSelectedMenuParent}
						onRadioChange={onMenuTreeRadioChange}
						onNodeClick={onNodeClick}
					/>
				</View>
			) : modalInfo.target === 'iconPicker' ? (
				<IconPicker
					onIconClick={iconName => {
						onIconPickerSubmit(iconName)
					}}
				/>
			) : modalInfo.target === 'duplicatedNameAlert' ? (
				<View css={style}>
					<Text category="p2">같은 경로에 동일한 폴더 이름을 사용할 수 없습니다.</Text>

					<View className="recommendedMenuNameView">
						<span className="recommendedLabel">추천 폴더이름</span>
						<Pressable
							className={`recommendedMenuNameButton ${
								isCheckedRecommendedName ? 'checked' : ''
							}`}
							onClick={() => setIsCheckedRecommendedName(!isCheckedRecommendedName)}
							aria-pressed={isCheckedRecommendedName}
						>
							<Icon name="check" className="checkIcon" />
							<span className="recommendedName">{unduplicatedName}</span>
						</Pressable>

						<span className="checkIndicatorText">
							{isCheckedRecommendedName
								? `'${unduplicatedName}' 폴더 이름이 선택 되었습니다.`
								: '선택된 것이 없습니다.'}
						</span>
					</View>
				</View>
			) : modalInfo.target === 'duplicatedNameWithMovingAlert' ? (
				<View>
					<Text category="p2">중복되는 이름이 발견되어 이동할 수 없습니다.</Text>
					<Text category="p2">
						이동할 폴더나 아이템의 이름을 변경하고 다시 시도해 보세요.
					</Text>
				</View>
			) : modalInfo.target === 'updatingSubmit' ? (
				<View>
					<Text category="p2">변경된 내용을 저장하시겠습니까?</Text>
				</View>
			) : modalInfo.target === 'deletingDirSubmit' ? (
				<View>
					{isAllowDeleteDir ? (
						<>
							<Text category="p2">하위 폴더들은 같이 삭제되며</Text>
							<Text category="p2">하위의 아이템 (웹사이트 컨텐츠)는 삭제되지 않고</Text>
							<Text category="p2">비활성 상태로 최상위 폴더로 이동됩니다.</Text>
							<Text category="p2">{"정말 이 메뉴를 '삭제' 하시겠습니까?"}</Text>
						</>
					) : (
						<Text category="p2">권한이 없습니다.</Text>
					)}
				</View>
			) : modalInfo.target === 'deletingObjectSubmit' ? (
				<View>
					{isAllowDeleteObject ? (
						<>
							<Text category="p2">이 메뉴 오브젝트를 삭제하면 메뉴에서 사라집니다.</Text>
							<Text category="p2">삭제를 진행하면 복구할 수 없습니다.</Text>
							<Text category="p2">{"정말 이 메뉴 오브젝트를 '삭제' 하시겠습니까?"}</Text>
						</>
					) : (
						<Text category="p2">권한이 없습니다.</Text>
					)}
				</View>
			) : null}
		</Modal>
	)
}

const style = css({
	fontFamily: boFont.ui,

	'.menuRootDirView': {
		position: 'relative',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		width: '100%',
		height: 36,
		borderBottom: `1px solid ${mono.line}`,

		'.menuRootDirButton': {
			display: 'flex',
			alignItems: 'center',
			gap: 8,
			height: 36,
			padding: 0,

			'.menuRootDirIcon': { fontSize: 18, color: mono.faint },
			'.menuRootDirText': { fontSize: 13, color: mono.ink },
		},

		'.menuRootDirRadiobutton': {
			marginRight: 4,
		},
	},

	'.recommendedMenuNameView': {
		display: 'flex',
		flexDirection: 'column',
		gap: 10,
		minWidth: '20rem',
		marginTop: 24,

		'.recommendedLabel': {
			fontSize: 11,
			letterSpacing: '.18em',
			textTransform: 'uppercase',
			fontWeight: 500,
			color: mono.faint,
		},

		'.recommendedMenuNameButton': {
			display: 'flex',
			alignItems: 'center',
			gap: 10,
			width: '100%',
			height: 40,
			padding: '0 12px',
			border: `1px solid ${mono.ink}`,
			borderRadius: 2,
			color: mono.ink,
			fontSize: 13,
			fontWeight: 500,
			transition: 'color 200ms ease-out, background-color 200ms ease-out',

			'.checkIcon': { fontSize: 16, color: 'inherit', opacity: 0.25 },

			'&.checked': {
				background: mono.ink,
				color: '#FFFFFF',
				'.checkIcon': { opacity: 1 },
			},
		},

		'.checkIndicatorText': {
			fontSize: 12,
			fontWeight: 300,
			color: mono.dim,
		},
	},
})
